import { 
  doc, 
  setDoc, 
  getDoc, 
  addDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  onSnapshot,
  writeBatch,
  increment,
  limit
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Creates or gets an existing conversation between two users
 * @param {string} currentUserId 
 * @param {string} targetUserId 
 * @param {object} currentUserData 
 * @param {object} targetUserData 
 * @returns {Promise<object>} Conversation object
 */
export const createOrGetConversation = async (currentUserId, targetUserId, currentUserData, targetUserData) => {
  // Create conversation ID (alphabetically sorted to ensure uniqueness)
  const conversationId = [currentUserId, targetUserId].sort().join('_');
  
  try {
    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationSnap = await getDoc(conversationRef);
    
    if (conversationSnap.exists()) {
      return { id: conversationSnap.id, ...conversationSnap.data() };
    } else {
      // Create new conversation
      const newConversation = {
        participants: [currentUserId, targetUserId],
        participantDetails: {
          [currentUserId]: {
            firstName: currentUserData.firstName || 'User',
            lastName: currentUserData.lastName || '',
            profilePicture: currentUserData.profilePicture || '',
            userType: currentUserData.userType || 'learner'
          },
          [targetUserId]: {
            firstName: targetUserData.firstName || 'User',
            lastName: targetUserData.lastName || '',
            profilePicture: targetUserData.profilePicture || '',
            userType: targetUserData.userType || 'provider'
          }
        },
        lastMessage: {
          senderId: '',
          senderName: 'System',
          content: 'Conversation started',
          timestamp: new Date(),
          messageType: 'system'
        },
        lastActivity: new Date(),
        unreadCount: {
          [currentUserId]: 0,
          [targetUserId]: 0
        },
        createdAt: new Date(),
        updatedAt: new Date(),
        isActive: true
      };
      
      await setDoc(conversationRef, newConversation);
      return { id: conversationId, ...newConversation };
    }
  } catch (error) {
    console.error('Error creating/getting conversation:', error);
    throw error;
  }
};

/**
 * Sends a message in a conversation
 * @param {string} conversationId 
 * @param {string} senderId 
 * @param {string} senderName 
 * @param {string} recipientId 
 * @param {string} content 
 * @param {string} messageType 
 * @returns {Promise<object>} Message object
 */
export const sendMessage = async (conversationId, senderId, senderName, recipientId, content, messageType = 'text') => {
  try {
    // Add message to messages collection
    const messageData = {
      conversationId,
      senderId,
      senderName,
      recipientId,
      content: content.trim(),
      messageType,
      timestamp: new Date(),
      isRead: false,
      readAt: null,
      isEdited: false,
      editedAt: null
    };
    
    const messageRef = await addDoc(collection(db, 'messages'), messageData);
    
    // Update conversation with last message and unread count
    const conversationRef = doc(db, 'conversations', conversationId);
    await updateDoc(conversationRef, {
      lastMessage: {
        senderId,
        senderName,
        content: content.trim(),
        timestamp: new Date(),
        messageType
      },
      lastActivity: new Date(),
      updatedAt: new Date(),
      [`unreadCount.${recipientId}`]: increment(1)
    });
    
    // Send email notification to recipient (fire and forget)
    try {
      const { sendMessageNotificationEmail } = await import('../utils/stripe');
      await sendMessageNotificationEmail(recipientId, {
        senderName,
        content: content.trim(),
        messageType,
        timestamp: new Date()
      });
      console.log('Message notification email sent successfully');
    } catch (emailError) {
      // Don't fail the message sending if email fails
      console.error('Failed to send message notification email:', emailError);
    }
    
    return { id: messageRef.id, ...messageData };
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

/**
 * Gets all conversations for a user
 * @param {string} userId 
 * @returns {Promise<array>} Array of conversation objects
 */
export const getUserConversations = async (userId) => {
  try {
    const q = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', userId),
      orderBy('lastActivity', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const conversations = [];
    
    querySnapshot.forEach((doc) => {
      conversations.push({ id: doc.id, ...doc.data() });
    });
    
    return conversations;
  } catch (error) {
    console.error('Error fetching conversations:', error);
    return [];
  }
};

/**
 * Gets messages in a conversation
 * @param {string} conversationId 
 * @param {number} messageLimit 
 * @returns {Promise<array>} Array of message objects
 */
export const getConversationMessages = async (conversationId, messageLimit = 50) => {
  try {
    const q = query(
      collection(db, 'messages'),
      where('conversationId', '==', conversationId),
      orderBy('timestamp', 'asc'),
      limit(messageLimit)
    );
    
    const querySnapshot = await getDocs(q);
    const messages = [];
    
    querySnapshot.forEach((doc) => {
      messages.push({ id: doc.id, ...doc.data() });
    });
    
    return messages;
  } catch (error) {
    console.error('Error fetching messages:', error);
    return [];
  }
};

/**
 * Marks messages as read in a conversation
 * @param {string} conversationId 
 * @param {string} userId 
 */
export const markMessagesAsRead = async (conversationId, userId) => {
  try {
    const q = query(
      collection(db, 'messages'),
      where('conversationId', '==', conversationId),
      where('recipientId', '==', userId),
      where('isRead', '==', false)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const batch = writeBatch(db);
      
      querySnapshot.forEach((doc) => {
        batch.update(doc.ref, {
          isRead: true,
          readAt: new Date()
        });
      });
      
      await batch.commit();
      
      // Reset unread count in conversation
      const conversationRef = doc(db, 'conversations', conversationId);
      await updateDoc(conversationRef, {
        [`unreadCount.${userId}`]: 0
      });
    }
  } catch (error) {
    console.error('Error marking messages as read:', error);
  }
};

/**
 * Subscribe to real-time conversation updates
 * @param {string} userId 
 * @param {function} callback 
 * @returns {function} Unsubscribe function
 */
export const subscribeToConversations = (userId, callback) => {
  console.log('Setting up conversation query for userId:', userId);
  
  // First try without orderBy in case composite index isn't set up
  let q;
  try {
    q = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', userId)
    );
  } catch (error) {
    console.error('Error creating query:', error);
    callback([]);
    return () => {};
  }
  
  return onSnapshot(q, (querySnapshot) => {
    console.log('Query snapshot received, size:', querySnapshot.size);
    const conversations = [];
    querySnapshot.forEach((doc) => {
      console.log('Found conversation:', doc.id, doc.data());
      conversations.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort manually by lastActivity if we can't use orderBy
    conversations.sort((a, b) => {
      const aTime = a.lastActivity?.toDate?.() || new Date(a.lastActivity) || new Date(0);
      const bTime = b.lastActivity?.toDate?.() || new Date(b.lastActivity) || new Date(0);
      return bTime - aTime;
    });
    
    console.log('Final conversations array:', conversations);
    callback(conversations);
  }, (error) => {
    console.error('Error listening to conversations:', error);
    callback([]);
  });
};

/**
 * Subscribe to real-time message updates in a conversation
 * @param {string} conversationId 
 * @param {function} callback 
 * @returns {function} Unsubscribe function
 */
export const subscribeToMessages = (conversationId, callback) => {
  console.log('Setting up message query for conversationId:', conversationId);
  
  // Try without orderBy first in case composite index isn't set up
  let q;
  try {
    q = query(
      collection(db, 'messages'),
      where('conversationId', '==', conversationId)
    );
  } catch (error) {
    console.error('Error creating message query:', error);
    callback([]);
    return () => {};
  }
  
  return onSnapshot(q, (querySnapshot) => {
    console.log('Message query snapshot received, size:', querySnapshot.size);
    const messages = [];
    querySnapshot.forEach((doc) => {
      console.log('Found message:', doc.id, doc.data());
      messages.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort manually by timestamp
    messages.sort((a, b) => {
      const aTime = a.timestamp?.toDate?.() || new Date(a.timestamp) || new Date(0);
      const bTime = b.timestamp?.toDate?.() || new Date(b.timestamp) || new Date(0);
      return aTime - bTime;
    });
    
    console.log('Final messages array:', messages);
    callback(messages);
  }, (error) => {
    console.error('Error listening to messages:', error);
    callback([]);
  });
};

/**
 * Gets the total unread message count for a user
 * @param {string} userId 
 * @returns {Promise<number>} Total unread count
 */
export const getUserUnreadCount = async (userId) => {
  try {
    const conversations = await getUserConversations(userId);
    let totalUnread = 0;
    
    conversations.forEach(conversation => {
      if (conversation.unreadCount && conversation.unreadCount[userId]) {
        totalUnread += conversation.unreadCount[userId];
      }
    });
    
    return totalUnread;
  } catch (error) {
    console.error('Error getting unread count:', error);
    return 0;
  }
};

/**
 * Formats a timestamp for display
 * @param {Date|Timestamp} timestamp 
 * @returns {string} Formatted time string
 */
export const formatMessageTime = (timestamp) => {
  if (!timestamp) return '';
  
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffMinutes < 1) {
    return 'Just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return date.toLocaleDateString();
  }
};

/**
 * Gets the other participant in a conversation
 * @param {object} conversation 
 * @param {string} currentUserId 
 * @returns {object} Other participant details
 */
export const getOtherParticipant = (conversation, currentUserId) => {
  const otherUserId = conversation.participants.find(id => id !== currentUserId);
  return conversation.participantDetails[otherUserId];
};